import React, { useEffect, useState } from "react";
import { useLazyQuery } from "@apollo/client";
import { useHistory } from "react-router-dom";
import { lang } from "../../../middleware/lang";
import { LoaderDots } from "../../../middleware/loaders";
import { HeaderTitle } from "../common/titles";
import {
  GET_LOW_STOCK_INGREDIENTS,
  GET_BRANCHES,
} from "./queries";
import { InventoryIngredient, CATEGORY_LABELS } from "./models";
import InventoryNav from "./inventoryNav";

const LowStockAlerts: React.FC = () => {
  const history = useHistory();
  const [branchFilter, setBranchFilter] = useState("");

  const [fetchAlerts, { data, loading }] = useLazyQuery(
    GET_LOW_STOCK_INGREDIENTS,
    { fetchPolicy: "no-cache" }
  );
  const [fetchBranches, { data: branchData }] = useLazyQuery(GET_BRANCHES, {
    fetchPolicy: "no-cache",
  });

  useEffect(() => {
    fetchBranches();
    //eslint-disable-next-line
  }, []);

  useEffect(() => {
    fetchAlerts({
      variables: {
        branch_id: branchFilter ? parseInt(branchFilter) : undefined,
      },
    });
    //eslint-disable-next-line
  }, [branchFilter]);

  const ingredients: InventoryIngredient[] =
    data?.getLowStockIngredients || [];

  const outOfStock = ingredients.filter(
    (i) => (i.total_stock || 0) <= 0
  );
  const lowStock = ingredients.filter(
    (i) => (i.total_stock || 0) > 0
  );

  return (
    <>
      <HeaderTitle
        title={lang("Alertas de Inventario", "Inventory Alerts")}
      />
      <InventoryNav active="alerts" />

      <div className="row mb-3">
        <div className="col-lg-3">
          <select
            className="custom-select"
            value={branchFilter}
            onChange={(e) => setBranchFilter(e.target.value)}
          >
            <option value="">
              {lang("Todas las sucursales", "All branches")}
            </option>
            {branchData?.getBranches?.map((b: any) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
        </div>
        <div className="col-lg-3 d-flex align-items-center">
          <button
            className="btn btn-outline-primary btn-sm"
            onClick={() => history.push("/inventory/purchases")}
          >
            <i className="fa fa-shopping-cart mr-1" />
            {lang("Compra Sugerida", "Suggested Purchase")}
          </button>
        </div>
      </div>

      {loading && <LoaderDots fullSize />}

      {data && (
        <>
          {/* Summary cards */}
          <div className="row mb-3">
            <div className="col-lg-4">
              <div className="material_card p-3 text-center">
                <small className="text-muted">
                  {lang("Sin Stock", "Out of Stock")}
                </small>
                <h3 className={outOfStock.length > 0 ? "text-danger" : ""}>
                  {outOfStock.length}
                </h3>
              </div>
            </div>
            <div className="col-lg-4">
              <div className="material_card p-3 text-center">
                <small className="text-muted">
                  {lang("Stock Bajo", "Low Stock")}
                </small>
                <h3 className={lowStock.length > 0 ? "text-warning" : ""}>
                  {lowStock.length}
                </h3>
              </div>
            </div>
            <div className="col-lg-4">
              <div className="material_card p-3 text-center">
                <small className="text-muted">
                  {lang("Total Alertas", "Total Alerts")}
                </small>
                <h3>{ingredients.length}</h3>
              </div>
            </div>
          </div>

          {/* Out of stock section */}
          {outOfStock.length > 0 && (
            <div className="row mb-3">
              <div className="col-lg-12">
                <div className="material_card p-3 border-left border-danger" style={{ borderLeftWidth: 4 }}>
                  <h5 className="text-danger mb-3">
                    <i className="fa fa-exclamation-circle mr-2" />
                    {lang("Sin Stock", "Out of Stock")}
                  </h5>
                  <div className="table-responsive">
                    <table className="table table-sm table-hover vertical_middle mb-0">
                      <thead className="thead-light">
                        <tr>
                          <th>{lang("Ingrediente", "Ingredient")}</th>
                          <th>{lang("Categoría", "Category")}</th>
                          <th className="text-center">
                            {lang("Stock", "Stock")}
                          </th>
                          <th className="text-center">
                            {lang("Umbral", "Threshold")}
                          </th>
                          <th className="text-center">
                            {lang("Punto Reorden", "Reorder Point")}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {outOfStock.map((ing) => (
                          <tr
                            key={ing.id}
                            style={{ cursor: "pointer" }}
                            onClick={() =>
                              history.push(`/inventory/edit/${ing.id}`)
                            }
                          >
                            <td className="font-weight-bold">
                              {ing.name}
                              <small className="text-muted ml-1">
                                ({ing.unit})
                              </small>
                            </td>
                            <td>
                              {ing.category ? (
                                <span className="badge badge-secondary">
                                  {lang(
                                    CATEGORY_LABELS[ing.category]?.label || ing.category,
                                    CATEGORY_LABELS[ing.category]?.labelEn || ing.category
                                  )}
                                </span>
                              ) : (
                                "—"
                              )}
                            </td>
                            <td className="text-center text-danger font-weight-bold">
                              {Number(ing.total_stock || 0).toFixed(2)}{" "}
                              {ing.unit}
                            </td>
                            <td className="text-center">
                              {Number(
                                ing.low_stock_threshold || 0
                              ).toFixed(2)}
                            </td>
                            <td className="text-center">
                              {Number(ing.reorder_point || 0).toFixed(2)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Low stock section */}
          {lowStock.length > 0 && (
            <div className="row mb-3">
              <div className="col-lg-12">
                <div className="material_card p-3 border-left border-warning" style={{ borderLeftWidth: 4 }}>
                  <h5 className="text-warning mb-3">
                    <i className="fa fa-exclamation-triangle mr-2" />
                    {lang("Stock Bajo", "Low Stock")}
                  </h5>
                  <div className="table-responsive">
                    <table className="table table-sm table-hover vertical_middle mb-0">
                      <thead className="thead-light">
                        <tr>
                          <th>{lang("Ingrediente", "Ingredient")}</th>
                          <th>{lang("Categoría", "Category")}</th>
                          <th className="text-center">
                            {lang("Stock", "Stock")}
                          </th>
                          <th className="text-center">
                            {lang("Umbral", "Threshold")}
                          </th>
                          <th className="text-center">
                            {lang("% Restante", "% Remaining")}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {lowStock.map((ing) => {
                          const stock = Number(ing.total_stock || 0);
                          const threshold = Number(
                            ing.low_stock_threshold || 1
                          );
                          const pct =
                            threshold > 0
                              ? Math.round((stock / threshold) * 100)
                              : 0;

                          return (
                            <tr
                              key={ing.id}
                              style={{ cursor: "pointer" }}
                              onClick={() =>
                                history.push(`/inventory/edit/${ing.id}`)
                              }
                            >
                              <td className="font-weight-bold">
                                {ing.name}
                                <small className="text-muted ml-1">
                                  ({ing.unit})
                                </small>
                              </td>
                              <td>
                                {ing.category ? (
                                  <span className="badge badge-secondary">
                                    {ing.category}
                                  </span>
                                ) : (
                                  "—"
                                )}
                              </td>
                              <td className="text-center text-warning font-weight-bold">
                                {stock.toFixed(2)} {ing.unit}
                              </td>
                              <td className="text-center">
                                {threshold.toFixed(2)}
                              </td>
                              <td className="text-center">
                                <div
                                  className="progress"
                                  style={{ height: 16 }}
                                >
                                  <div
                                    className={`progress-bar ${
                                      pct < 30
                                        ? "bg-danger"
                                        : pct < 60
                                        ? "bg-warning"
                                        : "bg-success"
                                    }`}
                                    style={{ width: `${Math.min(pct, 100)}%` }}
                                  >
                                    {pct}%
                                  </div>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* No alerts */}
          {ingredients.length === 0 && (
            <div className="row">
              <div className="col-lg-12">
                <div className="material_card p-4 text-center text-muted">
                  <i
                    className="fa fa-check-circle fa-3x mb-3"
                    style={{ color: "#48BB78" }}
                  />
                  <h5>
                    {lang(
                      "Todo en orden. No hay ingredientes con stock bajo.",
                      "All good. No low-stock ingredients."
                    )}
                  </h5>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default LowStockAlerts;
